<template>
  <base-slide-over ref="slideover" :title="title" size="5xl">
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-if="isContentLoading">
      <div class="tw-flex tw-my-8 tw-items-center" v-for="i in 3" :key="i">
        <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
          <div class="tw-flex tw-items-center">
            <base-skeleton-loader class="tw-w-3 tw-h-3" />
            <base-skeleton-loader class="tw-ml-3 tw-h-3 tw-w-56" />
          </div>
          <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
        </div>
        <base-skeleton-loader class="tw-flex-grow tw-h-3" />
      </div>
    </div>
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-else>
      <div class="tw-mb-4">
        <base-text-field v-model="searchQuery" placeholder="Search programs..." size="sm" @input="onSearchInput">
          <template #prefix>
            <search-icon class="tw-h-5 tw-w-5" />
          </template>
        </base-text-field>
      </div>

      <div class="tw-mt-4" v-if="isPaging">
        <div class="tw-flex tw-my-8 tw-items-center" v-for="i in skeletonCount" :key="i">
          <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
            <div class="tw-flex tw-items-center">
              <base-skeleton-loader class="tw-w-3 tw-h-3" />
              <base-skeleton-loader class="tw-ml-3 tw-h-3 tw-w-56" />
            </div>
            <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
          </div>
          <base-skeleton-loader class="tw-flex-grow tw-h-3" />
        </div>
      </div>

      <div v-else>
        <div v-for="program in pagedPrograms" :key="program.businessProgramId" class="tw-flex tw-items-center tw-mb-6">
          <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
            <div class="tw-flex-col">
              <base-checkbox :value="program.businessProgramId" v-model="selectedPrograms" :disabled="isPscSecondaryRole">
                <div class="tw-flex-col tw-my-auto">
                  {{ program.name }}
                  <div class="tw-text-gray-500 tw-text-xs tw-font-normal tw-mt-1">{{ program.programGroup }}</div>
                </div>
              </base-checkbox>
            </div>
            <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
          </div>
          <base-select
            class="tw-flex-grow"
            :options="getFilteredPlans(program.name)"
            label-key="name"
            value-key="businessPlanId"
            placeholder="Select a plan"
            :value="mappings[program.businessProgramId]"
            :disabled="isPscSecondaryRole"
            @input="handleInput(program.businessProgramId, $event)"
          />
        </div>
        <div v-if="pagedPrograms.length === 0" class="tw-text-gray-500 tw-text-center tw-py-4">
          No programs found.
        </div>
      </div>
    </div>

    <template #fixed-bottom>
      <div class="tw-px-3 tw-flex tw-justify-between" v-if="!isPscSecondaryRole">
        <div class="tw-flex tw-items-center">
          <base-button class="tw-py-2 tw-mr-2" type="flat" @click="selectAllVisible">
            {{ areAllVisibleSelected ? "Deselect All" : "Select All" }}
          </base-button>
          <base-button class="tw-py-2" type="flat" @click="selectAllEntries">
            {{ areAllEntriesSelected ? `Deselect all entries (${selectedPrograms.length})` : "Select all entries" }}
          </base-button>
        </div>
        <!-- Pagination controls -->
        <div class="tw-flex-1 tw-px-4">
          <base-pagination v-model="page" v-if="page.total > page.take" />
        </div>
        <div class="tw-py-2">
          <base-button type="flat" class="tw-w-auto tw-mr-3" @click="hide()">
            Cancel
          </base-button>
          <base-button :loading="isSaving" class="tw-w-auto" @click="save">
            Save
          </base-button>
        </div>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { ArrowRightIcon, SearchIcon } from "@/assets/svgs";
import { orderBy } from "lodash";
import { mapGetters } from "vuex";

export default {
  components: {
    ArrowRightIcon,
    SearchIcon,
  },
  props: {
    businessId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isContentLoading: false,
      isSaving: false,
      businessLocation: {},
      programs: [],
      plans: [],
      selectedPrograms: [],
      mappings: {},
      cachedOrderedPrograms: [],
      searchQuery: "",
      page: {
        take: 50,
        skip: 0,
        total: 0,
      },
      isPaging: false,
    };
  },
  computed: {
    ...mapGetters("role", ["isPscSecondaryRole"]),
    skeletonCount() {
      if (this.page.total === 0) return this.page.take;
      return Math.min(this.page.take, this.page.total - this.page.skip);
    },
    title() {
      return `${this.businessLocation.name} Mappings`;
    },
    orderedPlans() {
      return orderBy(this.plans, ["name"], ["asc"]);
    },
    areAllEntriesSelected() {
      return this.programs.length > 0 && this.selectedPrograms.length === this.programs.length;
    },
    areAllVisibleSelected() {
      if (this.pagedPrograms.length === 0) return false;
      return this.pagedPrograms.every((p) => this.selectedPrograms.includes(p.businessProgramId));
    },
    selectedSet() {
      return new Set(this.selectedPrograms);
    },
    filteredPrograms() {
      if (!this.searchQuery || !this.searchQuery.trim()) return this.cachedOrderedPrograms;
      const q = this.searchQuery.toLowerCase();
      return this.cachedOrderedPrograms.filter((p) => p.name.toLowerCase().includes(q) || (p.programGroup && p.programGroup.toLowerCase().includes(q)));
    },
    pagedPrograms() {
      const start = this.page.skip;
      return this.filteredPrograms.slice(start, start + this.page.take);
    },
  },
  watch: {
    programs(val) {
      this.cachedOrderedPrograms = orderBy(val, ["name", "programGroup"], ["asc", "asc"]);
      this.page.total = this.filteredPrograms.length;
    },
    page: {
      deep: true,
      handler(newVal, oldVal) {
        if (oldVal && (newVal.skip !== oldVal.skip || newVal.take !== oldVal.take)) {
          this.isPaging = true;
          setTimeout(() => {
            this.isPaging = false;
          }, 300);
        }
      },
    },
  },
  created() {
    this.loadAllInitialData();
  },
  methods: {
    onSearchInput() {
      this.page.skip = 0;
      this.page.total = this.filteredPrograms.length;
    },
    async loadAllInitialData() {
      this.isContentLoading = true;
      try {
        await this.getPlans();
      } catch (error) {
        console.error("Failed to load initial data:", error);
      } finally {
        this.isContentLoading = false;
      }
    },
    show(businessLocation) {
      this.businessLocation = businessLocation;
      this.$refs.slideover.show();
      this.selectedPrograms = [];
      this.searchQuery = "";
      this.page.skip = 0;
      this.getMappingsForLocation();
    },
    hide() {
      this.$refs.slideover.hide();
      this.selectedPrograms = [];
      this.mappings = {};
      this.businessLocation = {};
      this.isContentLoading = false;
    },
    async getPlans() {
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/plans/list`);
        this.plans = response.data;
      } catch (error) {
        console.error("Error fetching plans:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to load plans",
          message: `There was an error fetching the list of plans.`,
          error,
        });
        throw error;
      }
    },
    async getMappingsForLocation() {
      if (!this.businessLocation.businessLocationId) {
        console.warn("Cannot fetch mappings: businessLocation.businessLocationId is missing.");
        this.isContentLoading = false;
        return;
      }

      this.isContentLoading = true;
      try {
        const response = await this.$axios.get(`/businesses/${this.businessId}/locations/${this.businessLocation.businessLocationId}/mappings`);
        this.mappings = {};
        this.programs = response.data.map((m) => {
          this.mappings[m.businessProgramId] = m.businessPlanId;
          return {
            businessProgramId: m.businessProgramId,
            name: m.programName,
            programGroup: m.programGroup,
          };
        });

        this.$forceUpdate();
        this.isContentLoading = false;
      } catch (error) {
        console.error("Error fetching mappings:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to load mappings",
          message: `We were not able to load the mappings for this location.`,
          error,
        });
        this.isContentLoading = false;
        throw error;
      }
    },
    async save() {
      try {
        this.isSaving = true;
        await this.$axios.post(`/businesses/${this.businessId}/locations/${this.businessLocation.businessLocationId}/mappings`, { mappings: this.mappings });
        this.hide();
        this.selectedPrograms = [];
        this.mappings = {};
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to save mappings",
          message: `We were not able to save the mappings for this location. There may have been a connection error. Please reload the page to try again.`,
          error,
        });
        throw error;
      } finally {
        this.isSaving = false;
      }
    },
    handleInput(businessProgramId, businessPlanId) {
      if (this.selectedPrograms.includes(businessProgramId)) {
        this.selectedPrograms.forEach((programId) => {
          this.mappings[programId] = businessPlanId;
        });
      } else {
        this.mappings[businessProgramId] = businessPlanId;
      }
      this.$forceUpdate();
    },
    selectAllEntries() {
      this.selectedPrograms = this.areAllEntriesSelected ? [] : this.programs.map((l) => l.businessProgramId);
    },
    selectAllVisible() {
      const visibleIds = this.pagedPrograms.map((p) => p.businessProgramId);
      if (this.areAllVisibleSelected) {
        this.selectedPrograms = this.selectedPrograms.filter((id) => !visibleIds.includes(id));
      } else {
        const newSelected = new Set([...this.selectedPrograms, ...visibleIds]);
        this.selectedPrograms = Array.from(newSelected);
      }
    },
    extractCoreProgramName(programFullName) {
      if (!programFullName) return "";
      let coreName = programFullName.split("(")[0].trim();
      coreName = coreName.replace(/\s*\b(SL Tablet|Injection|Stand-Alone|Add-On|Nasal Spray|Starter Kit|TEST|SLIT|Testing|Kit)\b$/i, "").trim();
      coreName = coreName.replace(/\s*[-_]?\s*\b(TEST|SLIT|Injection|Kit|Testing)\b$/i, "").trim();
      coreName = coreName.replace(/^cmwl\s*\|\s*/i, "").trim();
      return coreName;
    },
    extractParentheticalPart(name) {
      const match = name.match(/\((.*?)\)/);
      return match ? match[1].toLowerCase().trim() : null;
    },
    extractProgramTypeSuffix(programFullName) {
      const match = programFullName.match(/\b(TEST|SLIT|INJECTION|TABLET)\b/i);
      return match ? match[1].toLowerCase() : null;
    },
    getCleanedPlanNameForMatching(planFullName) {
      if (!planFullName) return "";
      let cleanedName = planFullName;
      cleanedName = cleanedName
        .replace(/\s*(?:\|\s*\$\d+(?:\.\d{2})?|\$\d+(?:\.\d{2})?)\s*(- Dev Plan)?(?:\s*\|\s*(?:Starter|Maintenance|On-Going|Test Plan|Pre-Rollout|Phase\s*\d))?$/gi, "")
        .trim();
      cleanedName = cleanedName.replace(/\s*\|\s*(Starter|Maintenance|On-Going|Test Plan|Pre-Rollout|Phase\s*\d)\s*$/gi, "").trim();
      cleanedName = cleanedName.replace(/^cmwl\s*\|\s*/i, "").trim();
      return cleanedName;
    },
    getFilteredPlans(programName) {
      if (!this.orderedPlans || this.orderedPlans.length === 0) return [];

      const cleanProgramNameLower = programName.toLowerCase();
      const coreProgramName = this.extractCoreProgramName(programName).toLowerCase();
      const programTypeSuffix = this.extractProgramTypeSuffix(programName);
      const programParentheticalPart = this.extractParentheticalPart(programName);

      return this.orderedPlans.filter((plan) => {
        const cleanPlanName = this.getCleanedPlanNameForMatching(plan.name).toLowerCase();

        if (cleanProgramNameLower.startsWith("hawaii patients")) {
          return cleanPlanName.startsWith("hawaii");
        }

        if (programTypeSuffix) {
          const expectedPlanNameWithSuffix = `${coreProgramName} ${programTypeSuffix}`;
          if (cleanPlanName === expectedPlanNameWithSuffix.trim()) return true;
          if (cleanPlanName === coreProgramName) {
            const originalPlanNameLower = plan.name.toLowerCase();
            const suffixRegex = new RegExp(`\\b${programTypeSuffix}\\b`, "i");
            if (suffixRegex.test(originalPlanNameLower)) return true;
          }
        } else if (cleanPlanName === coreProgramName) {
          return true;
        }

        if (coreProgramName && cleanPlanName.startsWith(coreProgramName)) {
          if (programTypeSuffix) {
            const suffixRegex = new RegExp(`\\b${programTypeSuffix}\\b`, "i");
            if (suffixRegex.test(cleanPlanName)) return true;
          } else {
            return true;
          }
        }

        const programNameWithoutParenAndSuffix = programName
          .split("(")[0]
          .toLowerCase()
          .trim();
        let strippedProgramName = programNameWithoutParenAndSuffix
          .replace(/\s*\b(SL Tablet|Injection|Stand-Alone|Add-On|Nasal Spray|Starter Kit|TEST|SLIT|Testing|Kit)\b$/i, "")
          .trim();
        strippedProgramName = strippedProgramName.replace(/\s*[-_]?\s*\b(TEST|SLIT|Injection|Kit|Testing)\b$/i, "").trim();

        if (cleanPlanName === strippedProgramName && strippedProgramName.length > 0) return true;

        if (cleanPlanName.includes(strippedProgramName) && strippedProgramName.length > 0) {
          const planContainsSlit = /\b(slit)\b/.test(cleanPlanName);
          const programIsSlit = programTypeSuffix === "slit";
          const planContainsTest = /\b(test)\b/.test(cleanPlanName);
          const programIsTest = programTypeSuffix === "test";

          if (planContainsSlit && !programIsSlit) return false;
          if (planContainsTest && !programIsTest) return false;
          return true;
        }

        if (programParentheticalPart && cleanPlanName.includes(programParentheticalPart)) return true;

        return false;
      });
    },
  },
};
</script>
